<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';

import LabelItem from 'dashboard/components-next/Label/LabelItem.vue';
import AddLabel from 'dashboard/components-next/Label/AddLabel.vue';
import {
  cleanLabelTitle,
  groupLabelsByCustomAttribute,
} from 'dashboard/helper/commons.js';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const showDropdown = ref(false);

// Store the currently hovered label's ID
// Using JS state management instead of CSS :hover / group hover
// This will solve the flickering issue when hovering over the last label item
const hoveredLabel = ref(null);

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');
const contactAttributes = useMapGetter('attributes/getContactAttributes');

const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId);
  return allLabels.value.filter(({ title }) =>
    availableContactLabels.includes(title)
  );
});

const groupedLabels = computed(() =>
  groupLabelsByCustomAttribute(savedLabels.value, contactAttributes.value)
);

const toMenuItem = label => ({
  label: cleanLabelTitle(label),
  value: label.id,
  thumbnail: { name: cleanLabelTitle(label), color: label.color },
  isSelected: savedLabels.value.some(savedLabel => savedLabel.id === label.id),
  action: 'contactLabel',
});

// Las etiquetas de un atributo también se eligen desde aquí: la
// sincronización va en los dos sentidos, así que asignarlas escribe el
// atributo del contacto y quitarlas lo vacía. Van agrupadas bajo el nombre
// de su atributo para que se lean con contexto.
const labelMenuSections = computed(() => {
  const grouped = new Set();
  const sections = syncedListAttributes(contactAttributes.value)
    .map(attribute => {
      const labels = allLabels.value.filter(
        label => label.custom_attribute_definition_id === attribute.id
      );
      labels.forEach(label => grouped.add(label.id));
      return {
        title: attribute.attributeDisplayName,
        items: labels.map(toMenuItem),
      };
    })
    .filter(section => section.items.length);

  const others = allLabels.value
    .filter(label => !label.managed && !grouped.has(label.id))
    .map(toMenuItem)
    .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected));
  if (others.length) {
    sections.push({
      title: t('CONTACT_PANEL.LABELS.CONTACT.OTHERS_HEADER'),
      items: others,
    });
  }
  return sections;
});

const fetchLabels = async contactId => {
  if (!contactId) {
    return;
  }
  store.dispatch('contactLabels/get', contactId);
};

const handleLabelAction = async ({ value }) => {
  try {
    // Get current label titles
    const currentLabels = savedLabels.value.map(label => label.title);

    // Find the label title for the ID (value)
    const selectedLabel = allLabels.value.find(label => label.id === value);
    if (!selectedLabel) return;

    let updatedLabels;

    // If label is already selected, remove it (toggle behavior)
    if (currentLabels.includes(selectedLabel.title)) {
      updatedLabels = currentLabels.filter(
        labelTitle => labelTitle !== selectedLabel.title
      );
    } else {
      // Add the new label
      updatedLabels = [...currentLabels, selectedLabel.title];
    }

    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels: updatedLabels,
    });

    showDropdown.value = false;
  } catch (error) {
    // error
  }
};

const handleRemoveLabel = label => {
  return handleLabelAction({ value: label.id });
};

watch(
  () => props.contactId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fetchLabels(newVal);
    }
  }
);
onMounted(() => {
  if (route.params.contactId) {
    fetchLabels(route.params.contactId);
  }
});

const handleMouseLeave = () => {
  // Reset hover state when mouse leaves the container
  // This ensures all labels return to their default state
  hoveredLabel.value = null;
};

const handleLabelHover = labelId => {
  // Added this to prevent flickering on when showing remove button on hover
  // If the label item is at end of the line, it will show the remove button
  // when hovering over the last label item
  hoveredLabel.value = labelId;
};
</script>

<template>
  <div class="flex flex-col gap-3 w-full" @mouseleave="handleMouseLeave">
    <div
      v-for="group in groupedLabels.groups"
      :key="group.id"
      class="flex flex-col gap-1"
    >
      <span
        class="text-[10px] font-semibold text-n-slate-11 uppercase tracking-wider select-none"
      >
        {{ group.name }}
      </span>
      <div class="flex flex-wrap items-center gap-1.5">
        <LabelItem
          v-for="label in group.labels"
          :key="label.id"
          :label="label"
          :is-hovered="hoveredLabel === label.id"
          @remove="handleRemoveLabel"
          @hover="handleLabelHover(label.id)"
        />
      </div>
    </div>

    <div v-if="groupedLabels.otherLabels.length" class="flex flex-col gap-1">
      <span
        class="text-[10px] font-semibold text-n-slate-11 uppercase tracking-wider select-none"
      >
        {{ $t('CONTACT_PANEL.LABELS.CONTACT.OTHERS_HEADER') }}
      </span>
      <div class="flex flex-wrap items-center gap-1.5">
        <LabelItem
          v-for="label in groupedLabels.otherLabels"
          :key="label.id"
          :label="label"
          :is-hovered="hoveredLabel === label.id"
          @remove="handleRemoveLabel"
          @hover="handleLabelHover(label.id)"
        />
      </div>
    </div>

    <!-- Add Label -->
    <div class="flex items-center gap-2 mt-1">
      <AddLabel
        :label-menu-sections="labelMenuSections"
        @update-label="handleLabelAction"
      />
    </div>
  </div>
</template>
